import { Injectable, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import { AccountObject, Deal } from '@tradecafe/types/core'
import { DeepReadonly, isDealPortalVisible } from '@tradecafe/types/utils'
import { DocumentSetApiService } from 'src/api/document-lib/document-set'
import { OperationsApiService } from 'src/api/operations'
import { loadAccounts, selectAccountEntities } from 'src/app/store/accounts'
import { sendConfirmationSuccess } from 'src/app/store/deal-view.actions'
import { DealConfirmationCellComponent } from 'src/components/deal-confirmation-cell/deal-confirmation-cell.component'
import {
  BulkActionDialogComponent,
  BulkActionDialogConfig,
  BulkActionDialogData,
} from 'src/shared/bulk-action-dialog'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { waitNotEmpty } from '../data/utils'
import { DealValidationService } from './deal-validation.service'
import {
  DealConfirmationItem,
  getDealConfirmationAmounts,
  getBuyerName,
  getDealStatus,
  getSupplierName,
} from 'src/pages/admin/trading/deals/send-confirmation-form/send-confirmation-form.utils'
import { take } from 'rxjs/operators'

type AccountNameMap = DeepReadonly<Dictionary<AccountObject>>
interface SubmitDealOptions {
  skipConfirmationDialog?: boolean
}

@Injectable()
export class DealSubmitService {
  private toaster = inject(ToasterService)
  private DealValidation = inject(DealValidationService)
  private OperationsApi = inject(OperationsApiService)
  private DocumentSetApi = inject(DocumentSetApiService)
  private store = inject(Store)
  private dialog = inject(MatDialog)


  async submitDeal(deal: DeepReadonly<Deal>, options: SubmitDealOptions = {}) {
    const credit_checked = deal.deal_type === 'brokerage'
      ? undefined
      : await this.DealValidation.checkBuyerCredit(deal, 'creation')
    await this.DealValidation.askForNegativeMarginReason([deal])
    const [res] = await this.internalSubmitDeals([deal], [credit_checked], options)
    return res
  }

  async internalSubmitDeals(
    deals: DeepReadonly<Deal[]>,
    credit_checked: Deal['credit_checked'][],
    options: SubmitDealOptions = {},
  ) {
    if (!deals.length) return []

    const res: Deal[] = []

    const creditCheckedByDealId = new Map(
      deals.map((deal, index) => [deal.deal_id, credit_checked[index]]),
    )

    if (options.skipConfirmationDialog) {
      for (const deal of deals) {
        res.push(await this.confirmDeal(deal.deal_id, creditCheckedByDealId))
      }

      this.dispatchSubmissionSuccess(res)
      return res
    }

    this.store.dispatch(loadAccounts({}))

    const accountMap: AccountNameMap = await this.store.pipe(
      select(selectAccountEntities),
      waitNotEmpty(),
      take(1),
    ).toPromise()

    const items: DealConfirmationItem[] = deals.map((deal) => {
      const amounts = getDealConfirmationAmounts(deal)

      return {
        deal_id: deal.deal_id,
        buyerName: getBuyerName(deal, accountMap),
        supplierName: getSupplierName(deal, accountMap),
        estimatedAmount: amounts.estimatedAmount,
        estimatedCurrency: amounts.estimatedCurrency,
        actualAmount: amounts.actualAmount,
        actualCurrency: amounts.actualCurrency,
        amount: amounts.summaryAmount,
        currency: amounts.summaryCurrency,
        status: getDealStatus(deal),
        isClone: false,
        raw: deal,
      }
    })

    const config: BulkActionDialogConfig<DealConfirmationItem> = {
      title: deals.length === 1 ? 'Confirm Deal' : 'Confirm Deals',
      actionLabel: 'Confirm',
      processingLabel: 'Confirming',
      itemName: { singular: 'deal', plural: 'deals' },
      columns: [
        {
          id: 'deal_id',
          header: 'Deal #',
          width: '120px',
          getValue: (item) => item.deal_id,
        },
        {
          id: 'buyer',
          header: 'Buyer',
          minWidth: '140px',
          getValue: (item) => item.buyerName,
          cellComponent: DealConfirmationCellComponent,
          getComponentInputs: (item) => ({
            name: item.buyerName,
            status: item.status,
            isConfirmed: !!item.raw?.buyer_confirmed,
            isSent: !!item.raw?.attributes?.buyer_confirm_sent,
            isPortalVisible: isDealPortalVisible(item.raw, item.raw?.buyer_id),
          }),
        },
        {
          id: 'supplier',
          header: 'Supplier',
          minWidth: '140px',
          getValue: (item) => item.supplierName,
          cellComponent: DealConfirmationCellComponent,
          getComponentInputs: (item) => ({
            name: item.supplierName,
            status: item.status,
            isConfirmed: !!item.raw?.seller_confirmed,
            isSent: !!item.raw?.attributes?.seller_confirm_sent,
            isPortalVisible: isDealPortalVisible(item.raw, item.raw?.supplier_id),
          }),
        },
        {
          id: 'estimated_amount',
          header: 'Estimated Amount',
          minWidth: '130px',
          getValue: (item) => item.estimatedAmount?.toFixed(2) ?? '-',
          getSubValue: (item) => item.estimatedCurrency,
        },
        {
          id: 'actual_amount',
          header: 'Actual Amount',
          minWidth: '130px',
          getValue: (item) => item.actualAmount?.toFixed(2) ?? '-',
          getSubValue: (item) => item.actualCurrency,
        },
      ],
      excludedMessage: '',
      emptyMessage: 'No deals can be confirmed.',
      showProgress: true,
      dialogWidth: '850px',
      concurrency: 1,
      itemAction: async (item) => {
        res.push(await this.confirmDeal(item.deal_id, creditCheckedByDealId))
      },
    }

    const data: BulkActionDialogData<DealConfirmationItem> = {
      items,
      config,
    }

    const dialogRef = this.dialog.open(BulkActionDialogComponent, {
      data,
      width: config.dialogWidth,
      disableClose: true,
    })

    const result = await dialogRef.afterClosed().pipe(take(1)).toPromise()

    if (result) this.dispatchSubmissionSuccess(res)

    return res
  }

  private dispatchSubmissionSuccess(deals: Deal[]) {
    if (!deals.length) return

    this.store.dispatch(sendConfirmationSuccess({ dealIds: deals.map(deal => deal.deal_id) }))
    this.toaster.success(`${deals.length} deal${deals.length === 1 ? '' : 's'} submitted and locked`)
  }

  private async confirmDeal(
    dealId: string,
    creditCheckedByDealId: Map<Deal['deal_id'], Deal['credit_checked']>,
  ) {
    try {
      await this.DocumentSetApi.generateMissingDocument({ deal_ids: [dealId] })
    } catch (err) {
      console.error('Unable to create documents', err)
      this.toaster.error('Unable to create documents', err)
      throw err
    }

    try {
      return (await this.OperationsApi.confirmDeal(dealId, creditCheckedByDealId.get(dealId))).data
    } catch (err) {
      console.error(`Unable to submit Deal # ${dealId}`, err)
      this.toaster.error(`Unable to submit Deal # ${dealId}`, err)
      throw err
    }
  }
}
